import { useState, useEffect } from "react";
import { Chapter } from "@/data/bookOfComfort";
import { BookOpen, ChevronRight, List, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

interface BookTableOfContentsProps {
  chapters: Chapter[];
  darkMode?: boolean;
}

const BookTableOfContents = ({ chapters, darkMode = false }: BookTableOfContentsProps) => {
  const [activeChapter, setActiveChapter] = useState<number>(1);
  const [showMobile, setShowMobile] = useState(false);
  const [showMobileButton, setShowMobileButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;

      // Show mobile button when scrolled past hero section (500px)
      setShowMobileButton(window.scrollY > 500);

      // Find which chapter is currently in view
      for (let i = chapters.length - 1; i >= 0; i--) {
        const element = document.getElementById(`chapter-${chapters[i].id}`);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveChapter(chapters[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, [chapters]);

  const scrollToChapter = (chapterId: number) => {
    const element = document.getElementById(`chapter-${chapterId}`);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth"
      });
      setShowMobile(false); // Close mobile menu after selection
    }
  };

  return (
    <>
      {/* Desktop Table of Contents */}
      <div className="hidden lg:block sticky top-24 h-fit">
        <div className={cn(
          "rounded-lg shadow-md border p-6",
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        )}>
          {/* Header */}
          <div className={cn(
            "flex items-center gap-2 mb-4 pb-4 border-b",
            darkMode ? "border-gray-700" : "border-gray-200"
          )}>
            <BookOpen className="w-5 h-5 text-[#006B5F]" />
            <h3 className={cn(
              "font-semibold",
              darkMode ? "text-white" : "text-gray-900"
            )}>
              Table of Contents
            </h3>
          </div>

          {/* Chapter List */}
          <nav className="space-y-1">
            {chapters.map((chapter) => (
              <button
                key={chapter.id}
                onClick={() => scrollToChapter(chapter.id)}
                className={cn(
                  "w-full text-left px-3 py-2 rounded-md transition-all duration-200 group",
                  "hover:bg-[#006B5F]/5",
                  activeChapter === chapter.id
                    ? "bg-[#006B5F]/10 text-[#006B5F] font-medium"
                    : darkMode ? "text-gray-300" : "text-gray-600"
                )}
              >
                <div className="flex items-start gap-2">
                  <ChevronRight
                    className={cn(
                      "w-4 h-4 mt-0.5 flex-shrink-0 transition-transform",
                      activeChapter === chapter.id ? "text-[#006B5F]" : "text-gray-400",
                      "group-hover:translate-x-0.5"
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wide mb-0.5 opacity-60">
                      Chapter {chapter.id}
                    </div>
                    <div className="text-sm leading-tight line-clamp-2">
                      {chapter.title}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Floating Button */}
      {showMobileButton && (
        <Button
          onClick={() => setShowMobile(!showMobile)}
          className={cn(
            "lg:hidden fixed bottom-24 left-4 z-40 h-12 w-12 rounded-full shadow-lg",
            darkMode
              ? "bg-gray-800 hover:bg-gray-700 text-white border border-gray-700"
              : "bg-white hover:bg-gray-50 text-gray-900 border border-gray-200"
          )}
          title="Table of Contents"
        >
          {showMobile ? <X className="w-5 h-5" /> : <List className="w-5 h-5" />}
        </Button>
      )}

      {/* Mobile Table of Contents Panel */}
      {showMobile && (
        <>
          {/* Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/20 z-40"
            onClick={() => setShowMobile(false)}
          />

          {/* Panel */}
          <div className={cn(
            "lg:hidden fixed bottom-40 left-4 right-4 z-50 rounded-lg shadow-xl border p-4 max-h-[60vh] overflow-y-auto",
            darkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          )}>
            <div className={cn(
              "flex items-center gap-2 mb-3 pb-3 border-b",
              darkMode ? "border-gray-700" : "border-gray-200"
            )}>
              <BookOpen className="w-5 h-5 text-[#006B5F]" />
              <h3 className={cn(
                "font-semibold",
                darkMode ? "text-white" : "text-gray-900"
              )}>
                Chapters
              </h3>
            </div>

            <nav className="space-y-1">
              {chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => scrollToChapter(chapter.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md transition-all duration-200",
                    "hover:bg-[#006B5F]/5",
                    activeChapter === chapter.id
                      ? "bg-[#006B5F]/10 text-[#006B5F] font-medium"
                      : darkMode ? "text-gray-300" : "text-gray-600"
                  )}
                >
                  <div className="flex items-start gap-2">
                    <ChevronRight
                      className={cn(
                        "w-4 h-4 mt-0.5 flex-shrink-0",
                        activeChapter === chapter.id ? "text-[#006B5F]" : "text-gray-400"
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-wide mb-0.5 opacity-60">
                        Chapter {chapter.id}
                      </div>
                      <div className="text-sm leading-tight">
                        {chapter.title}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default BookTableOfContents;

